import { Mail, MessageSquare, Phone, Plus, Smartphone, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import type { Kontaktweg } from '../../lib/api/stammdaten';

const ARTEN: { wert: string; label: string; Icon: typeof Mail }[] = [
  { wert: 'mobil', label: 'Mobil', Icon: Smartphone },
  { wert: 'festnetz', label: 'Festnetz', Icon: Phone },
  { wert: 'email', label: 'E-Mail', Icon: Mail },
  { wert: 'sms', label: 'SMS', Icon: MessageSquare },
];

/**
 * Beliebig viele Kontaktwege — wie in der Personenakte (T-0302).
 *
 * Vorher gab es in manchen Masken genau ein Telefonfeld, in anderen eine Liste;
 * eine zweite Nummer oder Adresse ließ sich beim Verknüpfen und beim Anlegen
 * einer Probestunde gar nicht erfassen.
 */
export default function KontaktwegeEditor({
  idPrefix,
  wege,
  onChange,
  fehlt = false,
}: Readonly<{
  idPrefix: string;
  wege: Kontaktweg[];
  onChange: (wege: Kontaktweg[]) => void;
  fehlt?: boolean;
}>) {
  const aendern = (index: number, teil: Partial<Kontaktweg>) =>
    onChange(wege.map((weg, i) => (i === index ? { ...weg, ...teil } : weg)));

  return (
    <div className="space-y-2">
      {wege.map((weg, index) => {
        const Icon = ARTEN.find((a) => a.wert === weg.type)?.Icon ?? MessageSquare;
        return (
          <div key={`${idPrefix}-${index}`} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <select
              aria-label="Art des Kontaktwegs"
              className="h-9 w-32 shrink-0 rounded-md border border-input bg-transparent px-2 text-sm"
              value={weg.type}
              onChange={(e) => aendern(index, { type: e.target.value })}
            >
              {ARTEN.map((art) => (
                <option key={art.wert} value={art.wert}>
                  {art.label}
                </option>
              ))}
            </select>
            <Input
              id={index === 0 ? `${idPrefix}-kontakt` : undefined}
              aria-label="Kontaktweg"
              className={cn('min-w-[160px] flex-1', fehlt && 'border-destructive')}
              type={weg.type === 'email' ? 'email' : 'tel'}
              placeholder={weg.type === 'email' ? 'name@beispiel.de' : '+49 …'}
              value={weg.value || ''}
              onChange={(e) => aendern(index, { value: e.target.value })}
            />
            <label className="flex cursor-pointer select-none items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
              <Checkbox
                checked={weg.officeContactAllowed !== false}
                onCheckedChange={(an) => aendern(index, { officeContactAllowed: an === true })}
              />
              <span title="Darf das Büro diesen Kontaktweg nutzen?">Büro darf kontaktieren</span>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-destructive"
              aria-label="Kontaktweg entfernen"
              onClick={() => onChange(wege.filter((_, i) => i !== index))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      {wege.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine Kontaktwege hinterlegt.</p> : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...wege, { type: 'mobil', value: '', officeContactAllowed: true }])}
      >
        <Plus className="mr-1 h-3.5 w-3.5" />
        Kontaktweg hinzufügen
      </Button>
    </div>
  );
}
